import type { CLevelSynthesisRow, MonthIndex } from '../types/primes';

export const CLEVEL_PRIMES_EXPORT_ESTABLISHMENT = 'BLOOMAYS HUB';

const CSV_SEPARATOR = ';';

export type CLevelPrimesExportRow = {
  luccaId: string;
  firstname: string;
  lastname: string;
  commissionMonth: string;
  commission: number;
};

export type BuildCLevelValidatedPrimesExportResult = {
  rows: CLevelPrimesExportRow[];
};

const csvCell = (value: string): string => (/[",;\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

export const formatCLevelPrimesExportCommission = (amount: number): string =>
  `${amount.toLocaleString('fr-FR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: false,
  })}\u00A0€`;

export const buildCLevelValidatedPrimesExportMonth = (selectedYear: string, monthIndex: MonthIndex): string =>
  `${selectedYear}-${monthIndex}`;

const compareExportRows = (left: CLevelPrimesExportRow, right: CLevelPrimesExportRow): number => {
  const lastnameComparison = left.lastname.localeCompare(right.lastname, 'fr', { sensitivity: 'base' });
  if (lastnameComparison !== 0) {
    return lastnameComparison;
  }
  return left.firstname.localeCompare(right.firstname, 'fr', { sensitivity: 'base' });
};

export const buildCLevelValidatedPrimesExportRows = (
  rows: CLevelSynthesisRow[],
  monthIndex: MonthIndex,
  selectedYear: string,
): BuildCLevelValidatedPrimesExportResult => {
  const commissionMonth = buildCLevelValidatedPrimesExportMonth(selectedYear, monthIndex);

  const exportRows = rows.flatMap((row) => {
    if (row.rowStatus !== 'success') {
      return [];
    }

    const cell = row.months[monthIndex];
    if (cell.status !== 'value' || cell.valueSource !== 'validated' || cell.totalPrime === null) {
      return [];
    }

    const luccaId = row.luccaId?.trim();
    if (!luccaId) {
      return [];
    }

    return [
      {
        luccaId,
        firstname: row.tamFirstname,
        lastname: row.tamLastname,
        commissionMonth,
        commission: cell.totalPrime,
      },
    ];
  });

  return {
    rows: exportRows.sort(compareExportRows),
  };
};

export const clevelPrimesExportRowsToCsv = (rows: CLevelPrimesExportRow[]): string => {
  const header = ['CLE NAVETTE', 'Prénom', 'Nom', 'Etablissement', 'Mois de Commission', 'Commission']
    .map(csvCell)
    .join(CSV_SEPARATOR);

  const body = rows.map((row) =>
    [
      csvCell(row.luccaId),
      csvCell(row.firstname),
      csvCell(row.lastname),
      csvCell(CLEVEL_PRIMES_EXPORT_ESTABLISHMENT),
      csvCell(row.commissionMonth),
      formatCLevelPrimesExportCommission(row.commission),
    ].join(CSV_SEPARATOR),
  );

  return [header, ...body].join('\r\n');
};

export const buildCLevelValidatedPrimesExportFilename = (selectedYear: string, monthIndex: MonthIndex): string =>
  `export-navette-paie-${selectedYear}-${monthIndex}.csv`;

export type DownloadCLevelValidatedPrimesExportResult = BuildCLevelValidatedPrimesExportResult & {
  downloaded: boolean;
};

export const downloadCLevelValidatedPrimesExport = (
  rows: CLevelSynthesisRow[],
  monthIndex: MonthIndex,
  selectedYear: string,
): DownloadCLevelValidatedPrimesExportResult => {
  const exportResult = buildCLevelValidatedPrimesExportRows(rows, monthIndex, selectedYear);

  if (exportResult.rows.length === 0) {
    return {
      ...exportResult,
      downloaded: false,
    };
  }

  const blob = new Blob([`\uFEFF${clevelPrimesExportRowsToCsv(exportResult.rows)}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = buildCLevelValidatedPrimesExportFilename(selectedYear, monthIndex);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return {
    ...exportResult,
    downloaded: true,
  };
};
